import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { select, Store } from '@ngrx/store';
import { environment } from '@env/environment';
import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { DomainService } from '@core/services/domain.service';
import { EventEmitterService } from '@core/services/event-emitter.service';
import { MemberProfileHttpService } from '@core/entity/member-profile/service/member-profile.http.service';
import { IsLoggedIn } from '@core/entity/auth/state/auth.selectors';
import { SafeHtmlPipe } from '@shared/pipes/safe-html.pipe';
import { svgIconList } from '@shared/svg-icon-list';

@Component({
  selector: 'app-language-modal',
  standalone: true,
  imports: [CommonModule, TranslateModule, MatDialogModule, SafeHtmlPipe],
  templateUrl: './language-modal.component.html',
  styleUrl: './language-modal.component.scss',
})
export class LanguageModalComponent implements OnInit {

  local = localStorage;
  availableCountryAndLanguages: any;
  languages: any;

  isLoggedIn = false;
  currentCountryCode: string;
  currentLanguageCode: string;
  marketingCampaign = localStorage.getItem('marketing_campaign');
  currentDomain: string;

  svg: any = svgIconList;

  constructor(
    public dialogRef: MatDialogRef<LanguageModalComponent>,
    private dropdownHttpService: DropdownHttpService,
    private domainService: DomainService,
    private translateService: TranslateService,
    private memberHttpService: MemberProfileHttpService,
    private store: Store,
    private eventEmitterService: EventEmitterService,
  ) {
    this.availableCountryAndLanguages = this.dropdownHttpService.availableCountryAndLanguages;
    this.languages = this.dropdownHttpService.languages;

    // Locale is driven by the /:locale/ URL segment (parsed into DomainService), not localStorage.
    // Use the path-derived locale for the active-language highlight so it always reflects the URL.
    this.currentCountryCode = (this.domainService.getCountryCode() || environment.defaultCountryCode)?.toUpperCase();
    this.currentLanguageCode = (this.domainService.getLanguageCode() || environment.defaultLanguage)?.toUpperCase();
  }

  ngOnInit() {
    /* DomainService already matched the host against the (dynamic) domainSet during
       checkDomainAndLocale, so use its answer instead of string-searching the
       hardcoded map — which goes stale the moment a domain is rotated in BO.
       Falls back to domainName on the landing page, where currentDomainSet is null.
       Mirrors QPRO LocaleDialogComponent. */
    this.currentDomain = this.domainService.getCurrentDomainSet() ?? environment.domainName;

    this.store.pipe(select(IsLoggedIn)).subscribe(value => this.isLoggedIn = value);
  }

  onCloseDialog(event?: Event) {
    this.dialogRef.close();
  }

  setLanguage(languageCode: string, countryCode: string) {
    sessionStorage.removeItem('promotionSession');
    sessionStorage.removeItem('hotGameSession');
    sessionStorage.removeItem('slotSession');
    sessionStorage.removeItem('fishingSession');
    sessionStorage.removeItem('lotterySession');
    sessionStorage.removeItem('tableSession');
    sessionStorage.removeItem('casinoSession');
    sessionStorage.removeItem('top_depositors');

    // If change language then clear logo display and retreive from API again for new logo.
    if (localStorage.getItem('country_code') != countryCode || localStorage.getItem('language_code') != languageCode) {
      sessionStorage.removeItem('logo_favicon');
    }

    localStorage.setItem('country_code', countryCode);
    localStorage.setItem('language_code', languageCode);
    localStorage.setItem('locale', `${languageCode}-${countryCode}`.toLowerCase());

    this.eventEmitterService.onFlagEmitter();

    this.translateService.use(languageCode.toLowerCase());

    this.onCloseDialog();

    if (this.isLoggedIn) {
      this.memberHttpService.updateLocale().then(() => {
        let userData = JSON.parse(localStorage.getItem('user_data'));
        userData = { ...userData, lang_code: languageCode };
        localStorage.setItem('user_data', JSON.stringify(userData));

        // Keep the persisted ngrx memberProfile in step with the new locale too. Updating only
        // user_data left memberProfile on the old locale, so on reload the SetMemberProfile
        // effect (member-profile.effects) rewrote the URL back to that stale locale — reverting
        // the language switch. Sync lang_code/country_code/settings_locale_code so it matches.
        const profileRaw = localStorage.getItem('memberProfile');
        if (profileRaw) {
          const profile = {
            ...JSON.parse(profileRaw),
            country_code: countryCode.toUpperCase(),
            lang_code: languageCode.toUpperCase(),
            settings_locale_code: `${countryCode}_${languageCode}`.toUpperCase(),
          };
          localStorage.setItem('memberProfile', JSON.stringify(profile));
        }

        this.navigateToLocale(countryCode, languageCode);
      });
    } else {
      this.navigateToLocale(countryCode, languageCode);
    }
  }

  private navigateToLocale(countryCode: string, languageCode: string) {
    const newLocale = `${languageCode}-${countryCode}`.toLowerCase();
    let currentPath = window.location.pathname;
    if (/^\/[a-zA-Z]{2}-[a-zA-Z]{2}(\/|$)/.test(currentPath)) {
      currentPath = currentPath.replace(/^\/[a-zA-Z]{2}-[a-zA-Z]{2}/, '/' + newLocale);
    } else {
      currentPath = '/' + newLocale + (currentPath === '/' ? '/home' : currentPath);
    }

    // Preserve marketing/campaign params if present; drop legacy ?lang= (locale is in path now).
    const currentQueryParams = new URLSearchParams(window.location.search);
    currentQueryParams.delete('lang');
    if (this.marketingCampaign != null) {
      const marketingCampaignObject = JSON.parse(this.marketingCampaign);
      Object.keys(marketingCampaignObject).forEach(key => {
        // AIO-76147: 'campaign'/'dialog' are hidden from the address bar, so they
        // must not be put back here either - they stay in marketing_campaign.
        if (!['lang', 'campaign', 'dialog'].includes(key.toLowerCase())) {
          currentQueryParams.set(key, marketingCampaignObject[key]);
        }
      });
    }
    const queryString = currentQueryParams.toString();
    const url = currentPath + (queryString ? '?' + queryString : '');

    // Mirror the chosen locale into the `locale` cookie the SSR server reads. setLanguage only
    // updated localStorage; the stale cookie (previous locale) made the server override the new
    // /:locale/ path and bounce the page back to the old locale. writeLocaleCookie() is
    // server-only, so write it here before navigating. ('locale' === LocaleKey.)
    document.cookie = `locale=${newLocale}; path=/; max-age=31536000; samesite=lax`;

    window.location.assign(url);
  }

  returnZero() {
    return 0;
  }
}
